import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { FiEye, FiEyeOff } from "react-icons/fi";
import i18n from "./i18n";
import { useTranslation } from "react-i18next";
import InfoTooltip from "./InfoTooltip";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const Auth = ({ onLogin }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [icMattino, setIcMattino] = useState("");
  const [icPomeriggio, setIcPomeriggio] = useState("");
  const [icSera, setIcSera] = useState("");

  const [fsi, setFsi] = useState("");
  const [target, setTarget] = useState("");
  const [annoScoperta, setAnnoScoperta] = useState("");
  const [annoNascita, setAnnoNascita] = useState("");
  const [citta, setCitta] = useState("");
  const [peso, setPeso] = useState("");
  const [altezza, setAltezza] = useState("");
  const [device, setDevice] = useState("");
  const [centro, setCentro] = useState("");
  const [error, setError] = useState("");
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const [wizardStarted, setWizardStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [language, setLanguage] = useState(
    localStorage.getItem("lang") || i18n.language || "it"
  );

  const { t } = useTranslation();

  useEffect(() => {
    const savedLang = localStorage.getItem("lang");
    if (savedLang) setLanguage(savedLang);
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  const normalizeIC = (value) => {
    if (!value) return null;
    const trimmed = value.trim();
    const match = trimmed.match(/^1[\/\-.](\d{1,3})$/);
    if (match) return parseFloat(match[1]);
    return parseFloat(trimmed);
  };

  const registrationSteps = [
    {
      label: t("inserisciMattino"),
      value: icMattino,
      setter: setIcMattino,
      type: "text",
      placeholder: t("icMattino2"),
      tooltip: t("tooltipIcMattino"),
    },
    {
      label: t("inserisciPomeriggio"),
      value: icPomeriggio,
      setter: setIcPomeriggio,
      type: "text",
      placeholder: t("icPomeriggio2"),
      tooltip: t("tooltipIcPomeriggio"),
    },
    {
      label: t("inserisciSera"),
      value: icSera,
      setter: setIcSera,
      type: "text",
      placeholder: t("icSera2"),
      tooltip: t("tooltipIcSera"),
    },
    {
      label: t("inserisciFsi"),
      value: fsi,
      setter: setFsi,
      type: "number",
      placeholder: t("fsi"),
      tooltip: t("tooltipFsi"),
    },
    {
      label: t("inserisciTarget"),
      value: target,
      setter: setTarget,
      type: "number",
      placeholder: t("glicemiaTarget3"),
      tooltip: t("tooltipGlicemiaTarget"),
    },
    {
      label: t("annoScoperta1"),
      value: annoScoperta,
      setter: setAnnoScoperta,
      type: "number",
      placeholder: t("annoScoperta2"),
    },
    {
      label: t("annoNascita1"),
      value: annoNascita,
      setter: setAnnoNascita,
      type: "number",
      placeholder: t("annoNascita2"),
    },
    {
      label: t("citta1"),
      value: citta,
      setter: setCitta,
      type: "text",
      placeholder: t("citta2"),
    },
    {
      label: t("quantoPesi1"),
      value: peso,
      setter: setPeso,
      type: "number",
      placeholder: t("quantoPesi2"),
    },
    {
      label: t("quantoSeiAlto1"),
      value: altezza,
      setter: setAltezza,
      type: "number",
      placeholder: t("quantoSeiAlto2"),
    },
    {
      label: t("cheDispositivo1"),
      value: device,
      setter: setDevice,
      type: "text",
      placeholder: t("cheDispositivo2"),
    },
    {
      label: t("pressoQualeCentro1"),
      value: centro,
      setter: setCentro,
      type: "text",
      placeholder: t("pressoQualeCentro2"),
    },
    { label: t("disclaimer"), type: "disclaimer" },
  ];

  const nextStep = () => {
    setError("");
    if (!wizardStarted) {
      if (!email || !password || !confirmPassword) {
        setError(t("compilaCampi"));
        return;
      }
      if (password !== confirmPassword) {
        setError(t("passwordNonCorrispondono"));
        return;
      }
      setWizardStarted(true);
      setCurrentStep(0);
    } else {
      if (currentStep < registrationSteps.length - 1)
        setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (wizardStarted && currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const resetWizard = () => {
    setWizardStarted(false);
    setCurrentStep(0);
    setDisclaimerAccepted(false);
  };

  const handleAuth = async () => {
    setError("");
    setResendMessage("");
    try {
      if (isRegistering) {
        if (!wizardStarted || currentStep !== registrationSteps.length - 1) {
          setError(t("completaDati"));
          return;
        }
        if (!disclaimerAccepted) {
          setError(t("accettaDisclaimer"));
          return;
        }

        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
        });
        if (signUpError) throw signUpError;

        const userId = data.user?.id;
        if (!userId)
          throw new Error("Registrazione fallita: nessun userId disponibile");

        const { error: insertError } = await supabase.from("users").insert({
          id: userId,
          email,
          ic_mattino: icMattino ? normalizeIC(icMattino) : null,
          ic_pomeriggio: icPomeriggio ? normalizeIC(icPomeriggio) : null,
          ic_sera: icSera ? normalizeIC(icSera) : null,
          fsi: fsi ? parseFloat(fsi) : null,
          glicemia_target: target ? parseFloat(target) : null,
          anno_scoperta: annoScoperta ? parseInt(annoScoperta) : null,
          anno_nascita: annoNascita ? parseInt(annoNascita) : null,
          citta,
          peso: peso ? parseFloat(peso) : null,
          altezza: altezza ? parseFloat(altezza) : null,
          device,
          centro_diabetologico: centro,
        });
        if (insertError) throw insertError;

        if (!data.user.email_confirmed_at) {
          setIsRegistered(true);
          return;
        }
        onLogin(data.user);
      } else {
        const { data, error: signInError } =
          await supabase.auth.signInWithPassword({ email, password });

        if (signInError) {
          if (signInError.message.includes("Email not confirmed")) {
            setError(t("deviConfermareEmail"));
            return;
          }
          throw signInError;
        }
        if (!data.user) throw new Error(t("accessoFallito"));
        onLogin(data.user);
      }
    } catch (err) {
      setError(err.message || t("erroreAutenticazione"));
    }
  };

  const handleResendEmail = async () => {
    setResendMessage("");
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: "signup",
        email,
      });
      if (resendError) throw resendError;
      setResendMessage(t("mailReinviata"));
    } catch (err) {
      setResendMessage(err.message || t("erroreInvioMail"));
    }
  };

  if (isRegistered) {
    return (
      <div className="container">
        <img src="/logo.png" alt="CarboZen Logo" className="logo-image" />
        <div className="card-section card-green auth-card">
          <h2 className="titlesec">{t("registrazioneCompletata")}</h2>
          <p className="instructions">
            {t("mailConferma")} <strong>{email}</strong>.
            <br />
            {t("controllaPosta")}
          </p>
          <div className="wizard-buttons">
            <button onClick={handleResendEmail} className="custom-button">
              {t("reinviaMail")}
            </button>
            <button
              onClick={() => setIsRegistered(false)}
              className="custom-button"
            >
              {t("tornaLogin")}
            </button>
          </div>
          {resendMessage && (
            <p style={{ marginTop: "1rem", color: "green" }}>{resendMessage}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <a href="https://carbozen.it">
        <img src="/logo.png" alt="CarboZen Logo" className="logo-image" />
      </a>

      {!wizardStarted && (
        <div className="language-selector">
          <select
            value={language}
            onChange={(e) => {
              const lang = e.target.value;
              setLanguage(lang);
              i18n.changeLanguage(lang);
              localStorage.setItem("lang", lang);
            }}
            className="language-dropdown"
          >
            <option value="it">🇮🇹 Italiano</option>
            <option value="en">🇬🇧 English</option>
            <option value="es">🇪🇸 Español</option>
          </select>
        </div>
      )}

      <div className="card-section card-green auth-card">
        {/* LOGIN */}
        {!isRegistering && (
          <>
            <label className="form-label">
              <div className="input-wrapper">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="glicemia-input2"
                />
              </div>
            </label>
            <br />

            <label className="form-label">
            <div className="input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="glicemia-input2"
                style={{ paddingRight: "40px" }} // spazio per l’icona
              />
              <span
                className="eye-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </span>
            </div>
            </label>

            <br />

            <div className="auth-actions">
              <button onClick={handleAuth} className="custom-button">
                {t("accesso")}
              </button>

              <a href="/forgot-password" className="auth-link">
                {t("passwordDimenticata")}
              </a>

              <a
                href="#"
                className="auth-link"
                onClick={(e) => {
                  e.preventDefault();
                  setIsRegistering(true);
                  setWizardStarted(false);
                  setCurrentStep(0);
                }}
              >
                {t("registratiOra")}
              </a>
            </div>
          </>
        )}

        {/* REGISTRAZIONE (step iniziale email+password) */}
        {isRegistering && !wizardStarted && (
          <>
            <label className="form-label">
              <div className="input-wrapper">
                <input
                  type="email"
                  placeholder={t("inserisciMail")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="glicemia-input2"
                />
              </div>
            </label>
            <br />

            <label className="form-label">
              <div className="input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("inserisciPassword")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="glicemia-input2"
                  style={{ paddingRight: "40px" }}
                />
                <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
            </label>
            <br />

            <label className="form-label">
              <div className="input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t("confermaPassword")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="glicemia-input2"
                  style={{ paddingRight: "40px" }}
                />
                <span
                  className="eye-icon"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
            </label>

            <br />

              <div className="wizard-buttons">
                <button
                  type="button"
                  onClick={nextStep}
                  className="icon-nav"
                  aria-label={t("avanti")}
                >
                  <FiChevronRight size={34} />
                </button>
              </div>

              {isRegistering && !wizardStarted && (
                <p style={{ marginTop: "1rem", textAlign: "center" }}>
                  <a
                    href="#"
                    className="form-label"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsRegistering(false);
                      setWizardStarted(false);
                      setCurrentStep(0);
                    }}
                  >
                    {t("haiGiaAccount")}
                  </a>
                </p>
            )}
          </>
        )}

        {/* REGISTRAZIONE (wizard dati + disclaimer) */}
        {isRegistering && wizardStarted && (
          <>
            {registrationSteps[currentStep].type === "disclaimer" ? (
              <>
                <h3 className="titlesec">{t("disclaimer")}</h3>
                  <div className="disclaimer-row cz-check">
                    <input
                      type="checkbox"
                      id="disclaimer"
                      className="cz-checkbox"
                      checked={disclaimerAccepted}
                      onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                    />

                    <label htmlFor="disclaimer" className="disclaimer-label">
                      <span className="cz-box" aria-hidden="true"></span>

                      <span>
                        {t("dichiaroDisclaimer")}{" "}
                        <a
                          href="https://carbozen.it/disclaimer"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("disclaimer")}
                        </a>{" "}
                        {t("delSito")}
                      </span>
                    </label>
                  </div>
              </>
            ) : (
              <>
                <h3 className="titlesec">{t("datiUtente")}</h3>
                  <label className="form-label wizard-label">
                    <div className="label-with-tooltip">
                      <span>{registrationSteps[currentStep].label}</span>

                      {registrationSteps[currentStep].tooltip && (
                        <InfoTooltip text={registrationSteps[currentStep].tooltip} />
                      )}
                    </div>

                  <div className="input-wrapper">
                    <input
                      type={registrationSteps[currentStep].type}
                      placeholder={registrationSteps[currentStep].placeholder}
                      value={registrationSteps[currentStep].value}
                      onChange={(e) => registrationSteps[currentStep].setter(e.target.value)}
                      className="glicemia-input2"
                    />
                  </div>
                  </label>
              </>
            )}

            <div className="wizard-buttons">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="icon-nav"
                  aria-label={t("indietro")}
                >
                  <FiChevronLeft size={34} />
                </button>
              )}
              {currentStep < registrationSteps.length - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="icon-nav"
                  aria-label={t("avanti")}
                >
                  <FiChevronRight size={34} />
                </button>
              ) : (
                <button onClick={handleAuth} className="custom-button">
                  {t("registrati")}
                </button>
              )}
            </div>

          </>
        )}

        {error && (
          <p className="error" style={{ marginTop: "1rem", color: "red" }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default Auth;
